import React, { useState, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { FaSpinner } from 'react-icons/fa';

const emptyItem = { event_time: '', event_name: '', description: '', location: '', event_date: '' };

const EventSchedule = () => {
  const { t } = useTranslation();
  const { wedding } = useAppContext();

  const [item, setItem] = useState(emptyItem);
  const [editingId, setEditingId] = useState(null);
  const [editItem, setEditItem] = useState(emptyItem);
  const [eventSchedules, setEventSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cardId, setCardId] = useState(null);

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    let h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12;
    h = h ? h : 12; // the hour '0' should be '12'
    const m = minutes ? minutes.padStart(2, '0') : '00';
    return `${h}:${m} ${ampm}`;
  };

  const formatTimeForBackend = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const h = String(parseInt(hours, 10)).padStart(2, '0');
    const m = String(parseInt(minutes, 10)).padStart(2, '0');
    return `${h}:${m}`;
  };

  useEffect(() => {
    const storedCardId = localStorage.getItem('id');
    if (storedCardId) {
      setCardId(storedCardId);
    } else {
      setError('Card ID not found in local storage. Please ensure you have a card ID to manage schedules.');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (cardId) {
      fetchSchedules();
    }
  }, [cardId]);

  // Add sample data if eventSchedules is empty after initial fetch

  const fetchSchedules = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/planner/event-schedules?card_id=${cardId}`);
      setEventSchedules(response.data);
      console.log("Event: ", response.data);
    } catch (err) {
      console.error('Error fetching event schedules:', err);
      setError('Failed to fetch event schedules.');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async e => {
    e.preventDefault();
    if (item.event_time && item.event_name && item.event_date) {
      try {
        const formattedItem = { ...item, event_time: formatTimeForBackend(item.event_time), card_id: cardId };
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/planner/event-schedules`, formattedItem);
        setItem(emptyItem);
        fetchSchedules();
      } catch (err) {
        console.error('Error adding event schedule:', err);
        setError('Failed to add event schedule.');
      }
    }
  };

  const handleEdit = (scheduleItem) => {
    setEditingId(scheduleItem.id);
    setEditItem({
      event_time: scheduleItem.event_time,
      event_name: scheduleItem.event_name,
      description: scheduleItem.description,
      location: scheduleItem.location,
      event_date: scheduleItem.event_date,
    });
  };

  const handleEditSave = async (id) => {
    try {
      const formattedUpdatedItem = { ...editItem, event_time: formatTimeForBackend(editItem.event_time), card_id: cardId };
      await axios.put(`${process.env.REACT_APP_API_BASE_URL}/planner/event-schedules/${id}`, formattedUpdatedItem);
      setEditingId(null);
      setEditItem(emptyItem);
      fetchSchedules();
    } catch (err) {
      console.error('Error updating event schedule:', err.response ? err.response.data : err.message);
      setError('Failed to update event schedule.');
    }
  };

  const removeScheduleItem = async (id) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/planner/event-schedules/${id}`);
      fetchSchedules();
    } catch (err) {
      console.error('Error deleting event schedule:', err);
      setError('Failed to delete event schedule.');
    }
  };

  const clearAllSchedules = async () => {
    try {
      // This assumes your backend has an endpoint to delete all schedules for a card_id
      // If not, you might need to iterate and delete each one, or add a new backend route.
      // For now, let's assume a bulk delete endpoint or iterate.
      // Given the current API, we'll iterate for now.
      for (const schedule of eventSchedules) {
        await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/planner/event-schedules/${schedule.id}`);
      }
      fetchSchedules();
    } catch (err) {
      console.error('Error clearing all event schedules:', err);
      setError('Failed to clear all event schedules.');
    }
  };

  const sortedSchedule = [...(eventSchedules || [])].sort((a, b) => {
    const dateA = a.event_date || '';
    const dateB = b.event_date || '';
    const timeA = a.event_time || '';
    const timeB = b.event_time || '';

    if (dateA !== dateB) {
      return dateA.localeCompare(dateB);
    }
    return timeA.localeCompare(timeB);
  });

  if (error) return <div className="max-w-4xl mx-auto py-10 px-2 text-center text-danger">{error}</div>;
  if (!cardId) return <div className="max-w-4xl mx-auto py-10 px-2 text-center text-danger">{t('eventSchedule.noCardIdError')}</div>;

  return (
    <div className="max-w-4xl mx-auto py-10 px-2">
      <h1 className="text-3xl font-bold text-primary mb-4">{t('eventSchedule.title')}</h1>
      <p className="text-foreground/70 mb-4">
        {t('eventSchedule.description')}
        <br />
        <span className="text-sm text-danger">{t('eventSchedule.sampleDataNote')}</span>
      </p>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <form onSubmit={handleAdd} className="flex flex-col md:flex-row flex-wrap gap-3 w-full">
          <input type="date" className="flex-1 min-w-[180px] border rounded px-3 py-2 text-base sm:text-lg" value={item.event_date} onChange={e => setItem({ ...item, event_date: e.target.value })} required />
          <input type="time" className="flex-1 min-w-[120px] border rounded px-3 py-2 text-base sm:text-lg" value={item.event_time} onChange={e => setItem({ ...item, event_time: e.target.value })} required />
          <input type="text" className="flex-1 min-w-[200px] border rounded px-3 py-2 text-base sm:text-lg" placeholder={t('eventSchedule.titlePlaceholder')} value={item.event_name} onChange={e => setItem({ ...item, event_name: e.target.value })} required />
          <input type="text" className="flex-1 min-w-[200px] border rounded px-3 py-2 text-base sm:text-lg" placeholder={t('eventSchedule.locationPlaceholder')} value={item.location} onChange={e => setItem({ ...item, location: e.target.value })} />
          <input type="text" className="flex-1 min-w-[200px] border rounded px-3 py-2 text-base sm:text-lg" placeholder={t('eventSchedule.descriptionPlaceholder')} value={item.description} onChange={e => setItem({ ...item, description: e.target.value })} />
          
          {/* Button container with consistent alignment */}
          <div className="flex gap-3 w-full md:w-auto">
            <button type="submit" className="w-full md:w-[150px] btn-primary">{t('common.add')}</button>
            <button onClick={clearAllSchedules} className={`w-full md:w-[150px] bg-danger rounded-lg text-white px-6 py-2 transition-colors ${eventSchedules.length === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-600'}`} disabled={eventSchedules.length === 0}>{t('eventSchedule.clearAllSchedule')}</button>
          </div>
        </form>
      </div>

      {loading ? (
        <div className="max-w-4xl mx-auto py-10 px-2 text-center">
          <div className="flex justify-center items-center">
            <FaSpinner className="animate-spin text-primary text-4xl" />
          </div>
        </div>
      ):(
        <ul className="space-y-4">
          {sortedSchedule.length === 0 && <li className="text-foreground/50">{t('eventSchedule.noEventsScheduledYet')}</li>}
          {sortedSchedule.map((sched) => (
              <li key={sched.id} className="bg-white rounded shadow p-4 flex flex-col md:flex-row md:items-center gap-3">
                {editingId === sched.id ? (
                  <div className="flex flex-wrap gap-2 flex-1 w-full">
                    <input type="date" className="border rounded px-2 py-1 flex-1 min-w-[120px]" value={editItem.event_date} onChange={e => setEditItem({ ...editItem, event_date: e.target.value })} required />
                    <input type="time" className="border rounded px-2 py-1 flex-1 min-w-[100px]" value={editItem.event_time} onChange={e => setEditItem({ ...editItem, event_time: e.target.value })} required />
                    <input type="text" className="border rounded px-2 py-1 flex-1 min-w-[120px]" placeholder={t('eventSchedule.titlePlaceholder')} value={editItem.event_name} onChange={e => setEditItem({ ...editItem, event_name: e.target.value })} required />
                    <input type="text" className="border rounded px-2 py-1 flex-1 min-w-[120px]" value={editItem.location} onChange={e => setEditItem({ ...editItem, location: e.target.value })} placeholder={t('eventSchedule.locationPlaceholder')} />
                    <input type="text" className="border rounded px-2 py-1 flex-1 min-w-[120px]" value={editItem.description} onChange={e => setEditItem({ ...editItem, description: e.target.value })} placeholder={t('eventSchedule.descriptionPlaceholder')} />
                    <div className="flex gap-2 w-full md:w-auto">
                      <button onClick={() => handleEditSave(sched.id)} className="btn-primary flex-1">{t('common.save')}</button>
                      <button onClick={() => setEditingId(null)} className="text-danger px-2 flex-1">{t('common.cancel')}</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <span className="font-mono text-lg min-w-[70px]">{sched.event_date}</span>
                    <span className="font-mono text-lg min-w-[70px]">{formatTime(sched.event_time)}</span>
                    <span className="font-bold text-primary flex-1">{sched.event_name}</span>
                    {sched.location && <span className="text-info flex-1">{sched.location}</span>}
                    {sched.description && <span className="flex-1">{sched.description}</span>}
                    <div className='flex items-center justify-between'>
                      <button onClick={() => handleEdit(sched)} className="text-info px-2">{t('common.edit')}</button>
                      <button onClick={() => removeScheduleItem(sched.id)} className="text-danger px-2">{t('common.delete')}</button>
                    </div>
                  </>
                )}
              </li>
            ))}
        </ul>
      )}
    </div>
  );
};

export default EventSchedule;
